import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckIcon, XIcon, CheckCircleIcon, ChevronDownIcon, QuoteIcon, ShieldCheckIcon } from 'lucide-react';
import { OfferPackage } from '../../data/packages';
import { SectionDivider } from '../SectionDivider';
interface OfferPageProps {
  data: OfferPackage;
}
function FaqRow({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="bg-white rounded-lg border-l-[3px] border-emerald shadow-[0_4px_20px_rgba(12,35,64,0.08)] overflow-hidden" data-id="element-146">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between gap-4 p-6 text-left" aria-expanded={open} data-id="element-147">
        <span className="font-heading text-xl font-bold text-text-dark" data-id="element-148">
          {q}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-emerald flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-149" />
      </button>
      {open && <div className="px-6 pb-6" data-id="element-150">
          <p className="font-body text-base text-text-dark-secondary leading-relaxed" data-id="element-151">
            {a}
          </p>
        </div>}
    </div>;
}
export function OfferPage({
  data
}: OfferPageProps) {
  return <div className="w-full" data-id="element-152">
      {/* HERO */}
      <section className="bg-navy diagonal-pattern py-24 px-6 relative overflow-hidden" data-id="element-153">
        <div className="max-w-4xl mx-auto text-center relative" data-id="element-154">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-155">
            <div className="text-gold font-label text-xs uppercase tracking-label mb-6" data-id="element-156">
              {data.badge}
            </div>
            <h1 className="text-text-light font-heading text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-157">
              {data.hero.title}
            </h1>
            <p className="text-text-light-secondary font-body text-lg leading-relaxed mb-10 max-w-3xl mx-auto" data-id="element-158">
              {data.hero.subtitle}
            </p>
            <Link to="/contact" className="inline-block bg-gold text-navy px-8 py-4 rounded font-label text-sm uppercase tracking-label hover:bg-gold/90 transition-colors" data-id="element-159">
              Rezervă Sesiunea de Claritate
            </Link>
          </motion.div>
        </div>
      </section>

      {/* AWARENESS */}
      <section className="bg-cream py-20 px-6" data-id="element-160">
        <div className="max-w-4xl mx-auto" data-id="element-161">
          <div className="border-l-[3px] border-emerald pl-6 mb-8" data-id="element-162">
            <h2 className="text-text-dark font-heading text-3xl md:text-4xl font-bold" data-id="element-163">
              {data.awareness.title}
            </h2>
          </div>
          <div className="space-y-5" data-id="element-164">
            {data.awareness.paragraphs.map((p, i) => <p key={i} className="font-body text-lg text-text-dark-secondary leading-relaxed" data-id="element-165">
                {p}
              </p>)}
          </div>
        </div>
      </section>

      <div className="bg-cream" data-id="element-166">
        <SectionDivider data-id="element-167" />
      </div>

      {/* BENEFITS */}
      <section className="bg-cream py-20 px-6" data-id="element-168">
        <div className="max-w-5xl mx-auto" data-id="element-169">
          <h2 className="text-text-dark font-heading text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-170">
            {data.benefits.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-171">
            {data.benefits.items.map((item, i) => <div key={i} className="bg-white rounded-lg p-6 border-l-[3px] border-emerald shadow-[0_4px_20px_rgba(12,35,64,0.08)] flex items-start gap-4" data-id="element-172">
                <CheckCircleIcon className="w-6 h-6 text-emerald flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-173" />
                <p className="font-body text-base text-text-dark-secondary leading-relaxed" data-id="element-174">
                  {item}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-navy diagonal-pattern py-20 px-6" data-id="element-175">
        <div className="max-w-5xl mx-auto" data-id="element-176">
          <h2 className="text-text-light font-heading text-3xl md:text-4xl font-bold text-center mb-6" data-id="element-177">
            {data.howWeHelp.title}
          </h2>
          <p className="text-text-light-secondary font-body text-lg leading-relaxed text-center max-w-3xl mx-auto mb-12" data-id="element-178">
            {data.howWeHelp.intro}
          </p>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-179">
            {data.howWeHelp.items.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-180">
                <CheckIcon className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={2} data-id="element-181" />
                <p className="font-body text-base text-text-light-secondary leading-relaxed" data-id="element-182">
                  {item}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-cream py-20 px-6" data-id="element-183">
        <div className="max-w-4xl mx-auto" data-id="element-184">
          <h2 className="text-text-dark font-heading text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-185">
            {data.process.title}
          </h2>
          <div className="space-y-8" data-id="element-186">
            {data.process.steps.map((step, i) => <div key={i} className="flex gap-6" data-id="element-187">
                <div className="flex flex-col items-center" data-id="element-188">
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center" data-id="element-189">
                    <span className="text-gold font-heading text-xl font-bold" data-id="element-190">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  {i < data.process.steps.length - 1 && <div className="w-px flex-1 bg-gold/30 my-2" data-id="element-191"></div>}
                </div>
                <div className="pb-2" data-id="element-192">
                  <h3 className="font-heading text-2xl font-bold text-text-dark mb-2" data-id="element-193">
                    {step.title}
                  </h3>
                  <p className="font-body text-base text-text-dark-secondary leading-relaxed" data-id="element-194">
                    {step.text}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FOR WHOM / NOT */}
      <section className="bg-navy diagonal-pattern py-20 px-6" data-id="element-195">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-196">
          <div className="bg-dark-card rounded-xl p-8 border-t-[3px] border-emerald" data-id="element-197">
            <h3 className="font-heading text-2xl font-bold text-text-light mb-6 flex items-center gap-3" data-id="element-198">
              <CheckCircleIcon className="w-7 h-7 text-emerald" strokeWidth={1.5} data-id="element-199" />
              {data.forWhom.forTitle}
            </h3>
            <ul className="space-y-4" data-id="element-200">
              {data.forWhom.forItems.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-201">
                  <CheckIcon className="w-5 h-5 text-emerald flex-shrink-0 mt-1" strokeWidth={2} data-id="element-202" />
                  <span className="font-body text-base text-text-light-secondary leading-relaxed" data-id="element-203">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
          <div className="bg-dark-card rounded-xl p-8 border-t-[3px] border-gold" data-id="element-204">
            <h3 className="font-heading text-2xl font-bold text-text-light mb-6 flex items-center gap-3" data-id="element-205">
              <XIcon className="w-7 h-7 text-gold" strokeWidth={1.5} data-id="element-206" />
              {data.forWhom.notForTitle}
            </h3>
            <ul className="space-y-4" data-id="element-207">
              {data.forWhom.notForItems.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-208">
                  <XIcon className="w-5 h-5 text-gold flex-shrink-0 mt-1" strokeWidth={2} data-id="element-209" />
                  <span className="font-body text-base text-text-light-secondary leading-relaxed" data-id="element-210">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="bg-cream py-20 px-6" data-id="element-211">
        <div className="max-w-4xl mx-auto" data-id="element-212">
          <h2 className="text-text-dark font-heading text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-213">
            {data.included.title}
          </h2>
          <div className="bg-white rounded-lg p-8 md:p-10 border-l-[3px] border-emerald shadow-[0_4px_20px_rgba(12,35,64,0.08)] mb-10" data-id="element-214">
            <ul className="space-y-4" data-id="element-215">
              {data.included.items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-216">
                  <CheckCircleIcon className="w-6 h-6 text-emerald flex-shrink-0 mt-0.5" strokeWidth={1.5} data-id="element-217" />
                  <span className="font-body text-base text-text-dark-secondary leading-relaxed" data-id="element-218">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
          <div className="text-center" data-id="element-219">
            <Link to="/contact" className="inline-block bg-gold text-navy px-8 py-4 rounded font-label text-sm uppercase tracking-label hover:bg-gold/90 transition-colors mb-4" data-id="element-220">
              {data.included.ctaText}
            </Link>
            <p className="text-text-dark-secondary font-body text-sm max-w-xl mx-auto" data-id="element-221">
              {data.included.ctaSmall}
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (placeholder) */}
      <section className="bg-navy diagonal-pattern py-20 px-6" data-id="element-222">
        <div className="max-w-5xl mx-auto text-center" data-id="element-223">
          <h2 className="text-text-light font-heading text-3xl md:text-4xl font-bold mb-4" data-id="element-224">
            {data.testimonials.title}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-12" data-id="element-225">
            <div className="flex items-center gap-2 bg-dark-card/60 border border-gold/20 rounded-full px-5 py-2.5" data-id="element-226">
              <ShieldCheckIcon className="w-4 h-4 text-gold flex-shrink-0" strokeWidth={1.5} data-id="element-227" />
              <p className="text-text-light-secondary font-body text-sm" data-id="element-228">
                Din motive de confidențialitate, numele clienților sunt fictive.
                Testimonialele sunt reale.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-229">
            {[0, 1, 2].map(i => <div key={i} className="bg-dark-card rounded-xl p-8 border-t-[3px] border-gold text-left" data-id="element-230">
                <QuoteIcon className="w-10 h-10 text-gold mb-4 opacity-50" strokeWidth={1.5} data-id="element-231" />
                <p className="text-text-light-secondary font-body text-base italic leading-relaxed mb-6" data-id="element-232">
                  Testimonial în curs de colectare
                </p>
                <p className="text-text-light font-body font-medium" data-id="element-233">
                  Nume Client
                </p>
                <p className="text-text-light-secondary font-body text-sm" data-id="element-234">
                  Ocupație
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream py-20 px-6" data-id="element-235">
        <div className="max-w-3xl mx-auto" data-id="element-236">
          <h2 className="text-text-dark font-heading text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-237">
            {data.faq.title}
          </h2>
          <div className="space-y-4" data-id="element-238">
            {data.faq.items.map((item, i) => <FaqRow key={i} q={item.q} a={item.a} data-id="element-239" />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy diagonal-pattern py-20 px-6" data-id="element-240">
        <div className="max-w-3xl mx-auto text-center" data-id="element-241">
          <h2 className="text-text-light font-heading text-3xl md:text-4xl font-bold mb-6" data-id="element-242">
            Nu trebuie să știi totul. Trebuie doar să faci{' '}
            <span className="text-gold" data-id="element-243">primul pas</span>.
          </h2>
          <p className="text-text-light-secondary font-body text-lg leading-relaxed mb-8" data-id="element-244">
            Rezervă sesiunea de claritate și îți explic exact ce îți garantează
            legea în situația ta.
          </p>
          <Link to="/contact" className="inline-block bg-gold text-navy px-8 py-4 rounded font-label text-sm uppercase tracking-label hover:bg-gold/90 transition-colors" data-id="element-245">
            Rezervă Sesiunea de Claritate
          </Link>
        </div>
      </section>
    </div>;
}